// The nationality field: a searchable list of every nationality (lib/nationalities.ts — the same list the server holds the
// form to, shared/nationalities.ts). Type "ind" or "India" and pick "Indian"; ↑/↓ move, Enter picks, Escape closes, and
// leaving the box with an exact match typed ("british", "United Kingdom") picks it too. The value in and out is the
// nationality as a word ("British"), as the forms and Goodlord hold it; an older free-text value still shows until a
// nationality is picked. The list is position:fixed in a portal on <body>, so a drawer or a scrolling sheet never clips it
// and a <label> around the field never turns a click on a row into a click on the box. `kit` picks the
// styling of the form it sits in: the admin kit (Input) or the website kit (.site-input).
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Input } from './index';
import { findNationality, searchNationalities, type Nationality } from '../../lib/nationalities';

export function NationalitySelect({ value, onChange, kit = 'admin', required, disabled, invalid, placeholder, className = '', onBlur, 'aria-label': ariaLabel }: {
  value: string; onChange: (nationality: string) => void; kit?: 'admin' | 'site';
  required?: boolean; disabled?: boolean; invalid?: boolean; placeholder?: string; className?: string;
  onBlur?: () => void; 'aria-label'?: string;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const options = useMemo(() => searchNationalities(q), [q]);
  const known = findNationality(value);
  const shown = open ? q : known?.nationality ?? value ?? '';

  const start = () => {
    if (disabled || open) return;
    setQ('');
    setOpen(true);
    setActive(Math.max(0, searchNationalities('').findIndex((n) => n.nationality === known?.nationality)));
  };
  const close = () => { setOpen(false); setQ(''); };
  const pick = (n: Nationality) => { onChange(n.nationality); close(); };

  // Place the list under the box (above it when there is no room below), following scroll and resize.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 260), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const up = below < 220 && above > below;
      const maxHeight = Math.min(320, up ? above : below);
      setPos({ top: up ? r.top - 6 - maxHeight : r.bottom + 6, left, width, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  // Keep the highlighted one in view.
  useEffect(() => {
    if (open) listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { start(); return; }
      if (!options.length) return;
      setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % options.length : i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const n = options[active];
      if (n) pick(n);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };
  const onInputBlur = () => {
    // An exact match typed counts as picked ("british", "United Kingdom", "IE").
    const typed = q.trim() ? findNationality(q) : null;
    if (open && typed) onChange(typed.nationality);
    close();
    onBlur?.();
  };

  const props = {
    ref: inputRef,
    role: 'combobox' as const,
    'aria-expanded': open,
    'aria-controls': listId,
    'aria-autocomplete': 'list' as const,
    'aria-activedescendant': open && options[active] ? `${listId}-${active}` : undefined,
    'aria-invalid': invalid || undefined,
    'aria-label': ariaLabel,
    autoComplete: 'off',
    spellCheck: false,
    required,
    disabled,
    value: shown,
    placeholder: open ? (known ? known.nationality : 'Type to search') : placeholder ?? 'Choose your nationality',
    onFocus: start,
    onClick: start,
    onBlur: onInputBlur,
    onKeyDown,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => { if (!open) setOpen(true); setQ(e.target.value); setActive(0); },
  };
  const site = kit === 'site';

  return (
    <div className="relative min-w-0">
      {site
        ? <input {...props} className={`site-input pr-10 ${className}`} />
        : <Input {...props} className={`w-full pr-7 ${className}`} />}
      <ChevronDown aria-hidden className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-400 transition ${open ? 'rotate-180' : ''} ${site ? 'right-3.5 h-4 w-4' : 'right-2 h-3.5 w-3.5'}`} />
      {open && pos && createPortal(
        <ul
          ref={listRef} id={listId} role="listbox" aria-label="Nationalities"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className={`z-[200] overflow-y-auto overscroll-contain border border-ink-200 bg-white py-1 shadow-[0_12px_32px_-8px_rgba(20,20,20,0.18)] ${site ? 'rounded-[12px]' : 'rounded-lg'}`}
          // Keep the focus in the box while a row is pressed, so the blur does not close the list first.
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.length === 0 && <li className={`px-3 py-2.5 text-ink-400 ${site ? 'text-[14px]' : 'text-[12.5px]'}`}>No nationality matches “{q.trim()}”</li>}
          {options.map((n, i) => {
            const chosen = n.nationality === known?.nationality;
            return (
              <li
                key={n.code} id={`${listId}-${i}`} data-index={i} role="option" aria-selected={chosen}
                onMouseEnter={() => setActive(i)} onClick={() => pick(n)}
                className={`flex cursor-pointer items-baseline justify-between gap-3 ${site ? 'px-4 py-2.5 text-[15px]' : 'px-3 py-1.5 text-[12.5px]'} ${i === active ? 'bg-bronze-50' : ''}`}
              >
                <span className={`min-w-0 truncate ${chosen ? 'font-semibold text-bronze-700' : 'text-ink-900'}`}>{n.nationality}</span>
                <span className={`shrink-0 truncate text-ink-400 ${site ? 'text-[13px]' : 'text-[11.5px]'}`}>{n.country}</span>
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </div>
  );
}
